import React, { useState, useEffect, useMemo } from 'react';
import { fetchAllStores, createFlyer, createPage, createOffer, fetchAllVendors, createVendor, fetchAllCategories, fetchAllCategoriesFlat } from '../api/thamanService';
import callGeminiAPI from '../api/geminiAPI';
import { detectOffersPrompt, extractDetailsPrompt, createCategorizationPrompt } from '../utils/prompts';
import { categoryTree } from '../utils/categoryTree'; // Import the raw category tree for the prompt
import Loader from '../components/Loader';
import ErrorMessage from '../components/ErrorMessage';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import MultiCropEditorModal from '../components/MultiCropEditorModal';
import CategorySelector from '../components/CategorySelector';
import ImageModal from '../components/ImageModal';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// --- Helper Functions ---
const loadImage = (src) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(err);
    img.src = src;
});

const processPDF = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const pageImages = [];
    for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = document.createElement('canvas');
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        const context = canvas.getContext('2d');
        await page.render({ canvasContext: context, viewport }).promise;
        pageImages.push({
            pageNumber: i,
            imageDataUrl: canvas.toDataURL('image/jpeg', 0.9),
            height: viewport.height,
            width: viewport.width,
        });
    }
    return pageImages;
};

const dataURLtoFile = (dataUrl, filename) => {
    const arr = dataUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
};

const buildCodeMap = (subTree, mainCategoryName, map) => {
    Object.keys(subTree).forEach((subCategoryName, subIndex) => {
        const subCode = `S${subIndex + 1}`;
        subTree[subCategoryName].forEach((leafName, leafIndex) => {
            const leafCode = `${subCode}-${leafIndex + 1}`;
            const fullPath = `${mainCategoryName} > ${subCategoryName} > ${leafName}`;
            map.set(leafCode, fullPath);
        });
    });
};

const cropImage = (image, box_2d) => {
    const [ymin, xmin, ymax, xmax] = box_2d;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const cropWidth = ((xmax - xmin) / 1000) * image.width;
    const cropHeight = ((ymax - ymin) / 1000) * image.height;
    canvas.width = cropWidth;
    canvas.height = cropHeight;
    ctx.drawImage(
        image,
        (xmin / 1000) * image.width,  // source x
        (ymin / 1000) * image.height, // source y
        cropWidth,                   // source width
        cropHeight,                  // source height
        0,                           // dest x
        0,                           // dest y
        cropWidth,                   // dest width
        cropHeight                   // dest height
    );

    const croppedImgUrl = canvas.toDataURL('image/jpeg');
    const croppedBase64 = croppedImgUrl.split(',')[1];
    
    return { croppedImgUrl, croppedBase64 };
};


// --- Main Page Component ---
export default function AddFlyerPage() {
    const [processStage, setProcessStage] = useState('initial'); // 'initial', 'pages_processed', 'offers_detected', 'details_extracted', 'flyer_saved'
    const [stores, setStores] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [flatCategories, setFlatCategories] = useState([]);
    const [nestedCategories, setNestedCategories] = useState([]);
    const [selectedStoreId, setSelectedStoreId] = useState('');
    const [flyerFile, setFlyerFile] = useState(null);
    const [englishName, setEnglishName] = useState('');
    const [arabicName, setArabicName] = useState('');
    const [validFrom, setValidFrom] = useState('');
    const [validTo, setValidTo] = useState('');
    const [pdfPages, setPdfPages] = useState([]);
    const [offerBlocks, setOfferBlocks] = useState([]);
    const [productData, setProductData] = useState([]);
    const [newPagesMap, setNewPagesMap] = useState(new Map());
    const [multiCropModalState, setMultiCropModalState] = useState({ isOpen: false, page: null });
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [viewingImage, setViewingImage] = useState(null);
    const [failedItems, setFailedItems] = useState({
        detect: [], // Will store page indices that failed detection
        extract: [], // Will store block indices that failed extraction
        categorize: [], // Will store mainCategory names that failed categorization
    });

    useEffect(() => {
        const getInitialData = async () => {
            try {
                const storesData = await fetchAllStores();
                setStores(storesData);
                if (storesData.length > 0) setSelectedStoreId(storesData[0].id);

                const vendorsData = await fetchAllVendors();
                setVendors(vendorsData);

                const flatCategoryData = await fetchAllCategoriesFlat();
                setFlatCategories(flatCategoryData);

                const nestedCategoryData = await fetchAllCategories();
                setNestedCategories(nestedCategoryData);
            } catch (err) {
                setError('Could not load initial data for the form.');
            }
        };
        getInitialData();
    }, []);

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) setFlyerFile(e.target.files[0]);
    };

    // STAGE 1: Process the PDF into page images
    const handleProcessPDF = async () => {
        if (!flyerFile || !selectedStoreId || !validFrom || !validTo) {
            setError("Please upload a PDF, select a store, and choose both a 'Valid From' and 'Valid To' date.");
            return;
        }
        setIsLoading(true);
        setError(null);
        setLoadingMessage("Processing PDF to pages...");
        try {
            const pages = await processPDF(flyerFile);
            setPdfPages(pages);
            setProcessStage('pages_processed');
        } catch (err) {
            setError(`PDF processing failed: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // STAGE 2: Detect offer blocks on the pages
    const handleDetectOffers = async () => {
        setIsLoading(true);
        setError(null);
        // --- 👇 Clear previous detect failures when starting ---
        setFailedItems(prev => ({ ...prev, detect: [] }));
        let currentFailedItems = []; // Track failures in this run
        // --- END CLEAR ---
        try {
            const allBlocks = [];
            for (let i = 0; i < pdfPages.length; i++) {
                setLoadingMessage(`Detecting offers on page ${i + 1} of ${pdfPages.length}...`);
                const page = pdfPages[i];
                const base64ImageData = page.imageDataUrl.split(',')[1];
                
                let detectedBlocks = [];
                try {
                    detectedBlocks = await callGeminiAPI(detectOffersPrompt, base64ImageData);
                } catch (err) {
                    console.warn(`Attempt 1 failed for page ${i + 1}. Waiting 10s...`, err.message);
                    setLoadingMessage(`Page ${i + 1} failed. Retrying in 10s...`);
                    await delay(10000);
                    try {
                        detectedBlocks = await callGeminiAPI(detectOffersPrompt, base64ImageData);
                    } catch (finalErr) {
                        console.error(`FINAL ATTEMPT FAILED for page ${i + 1}.`, finalErr.message);
                        setError(`Offer detection failed on page ${i + 1}.`);
                        // --- 👇 Record the failure ---
                        currentFailedItems.push(i); // Store the index of the failed page
                        detectedBlocks = []; // Ensure loop continues
                        // --- END RECORD ---
                    }
                }
                
                allBlocks.push(...detectedBlocks.map(b => ({ ...b, pageNumber: page.pageNumber, imageDataUrl: page.imageDataUrl })));
            }
            
            setOfferBlocks(allBlocks); // Set successful blocks
            // --- 👇 Update state with failures and decide next step ---
            setFailedItems(prev => ({ ...prev, detect: currentFailedItems }));
            if (currentFailedItems.length === 0) {
                if (allBlocks.length === 0) {
                     setError("No offer blocks were detected on any pages.");
                     // Stay in 'pages_processed' stage if no blocks detected
                } else {
                    setProcessStage('offers_detected'); // Proceed if no failures
                }
            } // Otherwise, stay in 'pages_processed' to show retry buttons
            // --- END UPDATE ---
            
        } catch (err) {
            setError(`A critical error occurred during offer detection: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const offerBlocksByPage = useMemo(() => {
        return offerBlocks.reduce((acc, block) => {
            const pageNum = block.pageNumber;
            if (!acc[pageNum]) {
                acc[pageNum] = {
                    pageNumber: pageNum,
                    imageDataUrl: block.imageDataUrl,
                    offerBlocks: []
                };
            }
            acc[pageNum].offerBlocks.push(block);
            return acc;
        }, {});
    }, [offerBlocks]);

    const handleOpenMultiCropEditor = (pageNumber) => {
        setMultiCropModalState({ isOpen: true, page: offerBlocksByPage[pageNumber] });
    };

    const handleSaveMultiCrop = (pageNumber, newBox2dList) => {
        // Create new block objects from the saved box coordinates
        const newBlocksForPage = newBox2dList.map(box => ({
            box_2d: box,
            pageNumber: pageNumber,
            imageDataUrl: offerBlocksByPage[pageNumber].imageDataUrl
        }));

        // Remove all old blocks for this page and add the new ones
        const otherPagesBlocks = offerBlocks.filter(block => block.pageNumber !== pageNumber);
        setOfferBlocks([...otherPagesBlocks, ...newBlocksForPage]);
    };

    // STAGE 3: Extract product details from the blocks
    const handleExtractDetails = async () => {
        setIsLoading(true);
        setError(null);
        // --- 👇 Clear previous extract failures when starting ---
        setFailedItems(prev => ({ ...prev, extract: [] }));
        let currentFailedItems = []; // Track failures in this run
        // --- END CLEAR ---
        const allProducts = [];
        try {
            for (let i = 0; i < offerBlocks.length; i++) {
                setLoadingMessage(`Extracting details from offer ${i + 1} of ${offerBlocks.length}...`);
                const block = offerBlocks[i];
                
                // --- (Cropping logic is unchanged) ---
                const image = await loadImage(block.imageDataUrl);
                const { croppedImgUrl, croppedBase64 } = cropImage(image, block.box_2d);
    
                let extractedProducts = [];
                try {
                    extractedProducts = await callGeminiAPI(extractDetailsPrompt, croppedBase64);
                } catch (err) {
                    console.warn(`Attempt 1 failed for offer block ${i + 1}. Waiting 10s...`, err.message);
                    setLoadingMessage(`Offer ${i + 1} failed. Retrying in 10s...`);
                    await delay(10000);
                    try {
                        extractedProducts = await callGeminiAPI(extractDetailsPrompt, croppedBase64);
                    } catch (finalErr) {
                        console.error(`FINAL ATTEMPT FAILED for offer block ${i + 1}.`, finalErr.message);
                        setError(`Detail extraction failed for offer ${i + 1}.`);
                        // --- 👇 Record the failure ---
                        currentFailedItems.push(i); // Store the index of the failed block
                        // Push a placeholder error object
                        allProducts.push({
                            error: true, // Flag for UI
                            offerImage: croppedImgUrl,
                            pageNumber: block.pageNumber,
                            originalIndex: i // Keep track of which block failed
                        });
                        extractedProducts = []; // Ensure loop continues
                        // --- END RECORD ---
                    }
                }
                // Add successful products, plus page number and image
                allProducts.push(...extractedProducts.map(p => ({ ...p, offerImage: croppedImgUrl, pageNumber: block.pageNumber, originalIndex: i })));
            }
            
            setProductData(allProducts); // Set all products (including placeholders)
            // --- 👇 Update state with failures and decide next step ---
            setFailedItems(prev => ({ ...prev, extract: currentFailedItems }));
            if (currentFailedItems.length === 0) {
                setProcessStage('details_extracted'); // Proceed if no failures
            } // Otherwise, stay in 'offers_detected' to show retry buttons
            // --- END UPDATE ---
            
        } catch (err) {
            setError(`A critical error occurred during detail extraction: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleTableEdit = (productIndex, key, value) => {
        const newData = [...productData];
        const productToUpdate = { ...newData[productIndex] };
        
        if (key === 'sub_category') {
            // If we are updating the sub_category, we also need to update the top-level category
            productToUpdate.sub_category = value;
            productToUpdate.category = value ? value.split(' > ')[0] : '';
        } else {
            productToUpdate[key] = value;
        }

        newData[productIndex] = productToUpdate;
        setProductData(newData);
    };

    // STAGE 4: Save the Flyer and its Pages to the database
    const handleSaveFlyerAndPages = async () => {
        setIsLoading(true);
        setLoadingMessage('Saving flyer and pages to database...');
        setError(null);
        setSuccessMessage('');
        try {
            const flyerFormData = new FormData();
            flyerFormData.append('store_id', selectedStoreId);
            flyerFormData.append('english_name', englishName);
            flyerFormData.append('arabic_name', arabicName);
            flyerFormData.append('valid_from', validFrom || null);
            flyerFormData.append('valid_to', validTo || null);
            flyerFormData.append('flyer_pdf', flyerFile);
            const flyerResponse = await createFlyer(flyerFormData);

            const pagesMap = new Map();
            for (const page of pdfPages) {
                const pageFormData = new FormData();
                const pageImageFile = dataURLtoFile(page.imageDataUrl, `flyer_${flyerResponse.id}_page_${page.pageNumber}.jpg`);
                pageFormData.append('flyer_id', flyerResponse.id);
                pageFormData.append('page_number', page.pageNumber);
                pageFormData.append('height', page.height);
                pageFormData.append('width', page.width);
                pageFormData.append('page_image', pageImageFile);
                const pageResponse = await createPage(pageFormData);
                pagesMap.set(page.pageNumber, { id: pageResponse.id });
            }
            setNewPagesMap(pagesMap);
            setProcessStage('flyer_saved');
            setSuccessMessage('Flyer and pages saved successfully! You can now save the offers.');
        } catch (err) {
            setError(`Failed to save flyer/pages: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // STAGE 5: Save the extracted Offers to the database
    const handleSaveOffers = async () => {
        setIsLoading(true);
        setLoadingMessage(`Saving ${productData.length} offers...`);
        setError(null);
        let currentVendors = [...vendors];
        let successfulSaves = 0;
        let failedSaves = 0;
        try {
            for (const product of productData) {
                if (!product.english_name && !product.arabic_name) {
                    failedSaves++; continue;
                }
                if (!product.english_name) product.english_name = product.arabic_name;
                if (!product.arabic_name) product.arabic_name = product.english_name;
                let vendorId = null;
                if (product.vendor_english || product.vendor_arabic) {
                    const productEnglishLower = product.vendor_english ? product.vendor_english.toLowerCase().trim() : null;
                    const productArabicLower = product.vendor_arabic ? product.vendor_arabic.toLowerCase().trim() : null;
                    const existingVendor = currentVendors.find(v => (productEnglishLower && v.english_name && v.english_name.toLowerCase().trim() === productEnglishLower) || (productArabicLower && v.arabic_name && v.arabic_name.toLowerCase().trim() === productArabicLower));
                    if (existingVendor) {
                        vendorId = existingVendor.id;
                    } else {
                        if (product.vendor_english || product.vendor_arabic) {
                            const newVendor = await createVendor({ english_name: product.vendor_english || product.vendor_arabic, arabic_name: product.vendor_arabic || product.vendor_english });
                            vendorId = newVendor.id;
                            currentVendors.push(newVendor);
                        }
                    }
                }
                const pageInfo = newPagesMap.get(product.pageNumber);
                if (!pageInfo) {
                    failedSaves++; continue;
                }
                let categoryId = null;
                if (product.sub_category) {
                    const categoryPath = product.sub_category;
                    const leafCategoryName = categoryPath.split(' > ').pop().trim();
                    const foundCategory = flatCategories.find(cat => cat.english_name === leafCategoryName);
                    if (foundCategory) {
                        categoryId = foundCategory.id;
                    } else {
                        console.warn(`Could not find ID for leaf category: "${leafCategoryName}"`);
                    }
                }
                const offerFormData = new FormData();
                const offerImageFile = dataURLtoFile(product.offerImage, `offer_${product.english_name.replace(/\s/g, '_') || 'image'}.jpg`);
                offerFormData.append('item_image', offerImageFile);
                Object.keys(product).forEach(key => {
                    if (key !== 'offerImage' && product[key] != null) offerFormData.append(key, product[key]);
                });
                offerFormData.append('store_id', selectedStoreId);
                offerFormData.append('page_id', pageInfo.id);
                if (vendorId) offerFormData.append('vendor_id', vendorId);
                if (categoryId) offerFormData.append('category_id', categoryId);
                await createOffer(offerFormData);
                successfulSaves++;
            }
            setVendors(currentVendors);
            setSuccessMessage(`Save complete! ${successfulSaves} offers saved. ${failedSaves > 0 ? `${failedSaves} offers were skipped.` : ''}`);
            setProcessStage('initial');
            setProductData([]);
            setPdfPages([]);
            setOfferBlocks([]);
        } catch (err) {
            setError(`Failed to save offers: ${err.message}. ${successfulSaves} were saved before the error.`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeletePage = (pageNumberToDelete) => {
        if (window.confirm(`Are you sure you want to delete page ${pageNumberToDelete}? This cannot be undone.`)) {
            setPdfPages(prevPages => prevPages.filter(page => page.pageNumber !== pageNumberToDelete));
        }
    };

    const handleCategorizeOffers = async () => {
        setIsLoading(true);
        setLoadingMessage('Categorizing offers...');
        setError(null);
        
        setFailedItems(prev => ({ ...prev, categorize: [] }));
        let currentFailedItems = []; 
        
        try {
            // 1. Group products by their initial main category
            const groupedProducts = productData
                .filter(p => !p.error) 
                .reduce((acc, product, index) => {
                    const category = product.category; 
                    if (category && categoryTree[category]) {
                        if (!acc[category]) acc[category] = [];
                        // Crucial: we store the original index so we can update the correct item later
                        acc[category].push({ ...product, originalIndex: index }); 
                    }
                    return acc;
                }, {});
    
            const updatedProductData = [...productData];
            const mainCategories = Object.keys(groupedProducts);
            const totalGroups = mainCategories.length;
    
            for (let i = 0; i < totalGroups; i++) {
                const mainCategory = mainCategories[i];
                setLoadingMessage(`Categorizing ${mainCategory} (${i + 1}/${totalGroups})...`);
                
                const productsInGroup = groupedProducts[mainCategory];
                const subTree = categoryTree[mainCategory];
                if (!subTree) continue;
    
                // Process in batches of 15 for reliability
                const BATCH_SIZE = 15;
                let allCodedResultsForGroup = [];
    
                for (let chunkIndex = 0; chunkIndex * BATCH_SIZE < productsInGroup.length; chunkIndex++) {
                    const chunkStart = chunkIndex * BATCH_SIZE;
                    const chunkEnd = chunkStart + BATCH_SIZE;
                    const productChunk = productsInGroup.slice(chunkStart, chunkEnd);
                    
                    const prompt = createCategorizationPrompt(productChunk, subTree);
                    const blankImage = "R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"; 
                    
                    try {
                        const codedResultsChunk = await callGeminiAPI(prompt, blankImage);
                        if (Array.isArray(codedResultsChunk)) {
                            allCodedResultsForGroup.push(...codedResultsChunk);
                        }
                    } catch (err) {
                        console.warn(`Retry needed for ${mainCategory} batch ${chunkIndex + 1}`);
                        await delay(5000); // Wait 5s before single retry
                        try {
                            const codedResultsChunk = await callGeminiAPI(prompt, blankImage);
                            if (Array.isArray(codedResultsChunk)) {
                                allCodedResultsForGroup.push(...codedResultsChunk);
                            }
                        } catch (finalErr) {
                            if (!currentFailedItems.includes(mainCategory)) {
                                 currentFailedItems.push(mainCategory);
                            }
                        }
                    }
                }
    
                // 2. MAPPING LOGIC: Map AI codes (["P1", "S1-1"]) back to full paths
                const categoryCodeMap = new Map();
                buildCodeMap(subTree, mainCategory, categoryCodeMap);
                
                allCodedResultsForGroup.forEach(entry => {
                    // entry is ["P1", "S1-1"]
                    if (!Array.isArray(entry) || entry.length < 2) return;
                    
                    const pCode = entry[0]; 
                    const cCode = entry[1]; 

                    const productMatch = pCode?.match(/\d+/);
                    if (!productMatch) return;
                    
                    const productIndexInGroup = parseInt(productMatch[0]) - 1; 
                    const originalProductIndex = productsInGroup[productIndexInGroup]?.originalIndex; 
                    
                    const categoryPath = categoryCodeMap.get(cCode);
                    if (originalProductIndex !== undefined && categoryPath) {
                        updatedProductData[originalProductIndex].sub_category = categoryPath;
                        // Ensure top-level category is synced
                        updatedProductData[originalProductIndex].category = categoryPath.split(' > ')[0];
                    }
                });
            }
    
            setProductData(updatedProductData); 
            setFailedItems(prev => ({ ...prev, categorize: currentFailedItems }));
            
            if (currentFailedItems.length === 0) {
                setProcessStage('categorized');
            }
            
        } catch (err) {
            setError(`Categorization error: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRetryDetect = async () => {
        setIsLoading(true);
        setError(null);
        let stillFailing = [];
        const indicesToRetry = failedItems.detect; // Get indices from state
        try {
            const updatedBlocks = [...offerBlocks]; // Start with existing successful blocks
            for (const index of indicesToRetry) {
                 setLoadingMessage(`Retrying offer detection on page index ${index}...`);
                 const page = pdfPages[index]; // Get the specific page that failed
                 const base64ImageData = page.imageDataUrl.split(',')[1];
                 let detectedBlocksRetry = [];
                 try {
                     detectedBlocksRetry = await callGeminiAPI(detectOffersPrompt, base64ImageData);
                     await delay(1000); // Add a small delay between retries
                 } catch (retryErr) {
                     console.error(`RETRY FAILED for page index ${index}.`, retryErr.message);
                     setError(`Retry failed for page index ${index}.`);
                     stillFailing.push(index); // Keep it in the failed list
                     detectedBlocksRetry = [];
                 }
                 // Add successfully retried blocks
                 updatedBlocks.push(...detectedBlocksRetry.map(b => ({ ...b, pageNumber: page.pageNumber, imageDataUrl: page.imageDataUrl })));
            }
            setOfferBlocks(updatedBlocks); // Update with retried blocks
            setFailedItems(prev => ({ ...prev, detect: stillFailing })); // Update failures
            if (stillFailing.length === 0 && updatedBlocks.length > 0) {
                setProcessStage('offers_detected'); // Proceed if all retries succeeded
            }
        } catch(err) {
             setError(`A critical error occurred during retry: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    };
    
    const handleRetryExtract = async () => {
        setIsLoading(true);
        setError(null);
        let stillFailing = [];
        const indicesToRetry = failedItems.extract;
        try {
            const updatedProductData = [...productData]; // Start with existing data
            for (const index of indicesToRetry) {
                 setLoadingMessage(`Retrying detail extraction for block index ${index}...`);
                 const block = offerBlocks[index]; // Get the specific block that failed
                 // --- (Cropping logic) ---
                const image = await loadImage(block.imageDataUrl);
                const { croppedImgUrl, croppedBase64 } = cropImage(image, block.box_2d);
                
                 let extractedProductsRetry = [];
                 try {
                     extractedProductsRetry = await callGeminiAPI(extractDetailsPrompt, croppedBase64);
                     await delay(1000);
                 } catch (retryErr) {
                     console.error(`RETRY FAILED for block index ${index}.`, retryErr.message);
                     setError(`Retry failed for block index ${index}.`);
                     stillFailing.push(index);
                     // Keep the placeholder error object in updatedProductData
                     continue; // Skip updating this item
                 }
                 // Find the placeholder and replace it with successful data
                 const placeholderIndex = updatedProductData.findIndex(p => p.error && p.originalIndex === index);
                 if (placeholderIndex !== -1) {
                     // Replace placeholder with potentially multiple extracted products
                     const productsToAdd = extractedProductsRetry.map(p => ({ ...p, offerImage: croppedImgUrl, pageNumber: block.pageNumber, originalIndex: index }));
                     updatedProductData.splice(placeholderIndex, 1, ...productsToAdd);
                 } else {
                    // This shouldn't happen if placeholder was added correctly, but as fallback:
                    updatedProductData.push(...extractedProductsRetry.map(p => ({ ...p, offerImage: croppedImgUrl, pageNumber: block.pageNumber, originalIndex: index })));
                 }
            }
            setProductData(updatedProductData); // Update with retried data
            setFailedItems(prev => ({ ...prev, extract: stillFailing }));
            if (stillFailing.length === 0) {
                setProcessStage('details_extracted');
            }
        } catch(err) {
             setError(`A critical error occurred during retry: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    };
    
    const handleRetryCategorize = async () => {
        setIsLoading(true);
        setLoadingMessage('Retrying categorization...');
        setError(null);
        let stillFailing = [];
        const categoriesToRetry = [...failedItems.categorize];

        try {
            const updatedProductData = [...productData];

            for (const mainCategory of categoriesToRetry) {
                setLoadingMessage(`Retrying ${mainCategory}...`);
                
                // Get products belonging to this category that don't have a sub_category yet
                const productsInGroup = updatedProductData
                    .map((p, idx) => ({ ...p, originalIndex: idx }))
                    .filter(p => p.category === mainCategory && !p.sub_category && !p.error);

                if (productsInGroup.length === 0) continue;

                const subTree = categoryTree[mainCategory];
                if (!subTree) continue;

                const BATCH_SIZE = 10; // Smaller batch size for retries
                let allCodedResultsForGroup = [];
                let hasFailure = false;

                for (let i = 0; i < productsInGroup.length; i += BATCH_SIZE) {
                    const chunk = productsInGroup.slice(i, i + BATCH_SIZE);
                    const prompt = createCategorizationPrompt(chunk, subTree);
                    const blankImage = "R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

                    try {
                        const codedResultsChunk = await callGeminiAPI(prompt, blankImage);
                        if (Array.isArray(codedResultsChunk)) {
                            allCodedResultsForGroup.push(...codedResultsChunk);
                        }
                    } catch (e) {
                        hasFailure = true;
                        break;
                    }
                }

                if (hasFailure) {
                    stillFailing.push(mainCategory);
                    continue;
                }

                // Mapping
                const categoryCodeMap = new Map();
                buildCodeMap(subTree, mainCategory, categoryCodeMap);
                
                allCodedResultsForGroup.forEach(entry => {
                    if (!Array.isArray(entry) || entry.length < 2) return;
                    const [pCode, cCode] = entry;
                    const productMatch = pCode?.match(/\d+/);
                    if (!productMatch) return;
                    
                    const productIndexInGroup = parseInt(productMatch[0]) - 1;
                    const originalProductIndex = productsInGroup[productIndexInGroup]?.originalIndex;
                    const categoryPath = categoryCodeMap.get(cCode);
                    
                    if (originalProductIndex !== undefined && categoryPath) {
                        updatedProductData[originalProductIndex].sub_category = categoryPath;
                        updatedProductData[originalProductIndex].category = categoryPath.split(' > ')[0];
                    }
                });
            }

            setProductData(updatedProductData);
            setFailedItems(prev => ({ ...prev, categorize: stillFailing }));
            if (stillFailing.length === 0) setProcessStage('categorized');
            
        } catch (err) {
            setError(`Retry failed: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div>
            <h1 className="text-3xl font-bold mb-6">Add New Flyer</h1>
            {error && <ErrorMessage message={error} />}
            {successMessage && <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg my-4">{successMessage}</div>}

            <div className="bg-white p-8 rounded-lg shadow-md space-y-6 mb-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-gray-700 font-medium mb-2">1. Upload Flyer PDF</label>
                        <input type="file" onChange={handleFileChange} accept=".pdf" disabled={processStage !== 'initial'} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50" required />
                    </div>
                    <div>
                        <label className="block text-gray-700 font-medium mb-2">2. Select Store</label>
                        <select value={selectedStoreId} onChange={(e) => setSelectedStoreId(e.target.value)} disabled={processStage !== 'initial'} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 disabled:opacity-50" required>
                            {stores.map(store => (<option key={store.id} value={store.id}>{store.english_name} / {store.arabic_name}</option>))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-gray-700 font-medium mb-2">English Name (Optional)</label>
                        <input type="text" value={englishName} onChange={(e) => setEnglishName(e.target.value)} disabled={processStage !== 'initial'} className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50" />
                    </div>
                    <div>
                        <label className="block text-gray-700 font-medium mb-2">Arabic Name (Optional)</label>
                        <input type="text" value={arabicName} onChange={(e) => setArabicName(e.target.value)} disabled={processStage !== 'initial'} className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50" />
                    </div>
                    <div>
                        <label className="block text-gray-700 font-medium mb-2">Valid From</label>
                        <input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} disabled={processStage !== 'initial'} className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50" required />
                    </div>
                    <div>
                        <label className="block text-gray-700 font-medium mb-2">Valid To</label>
                        <input type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} disabled={processStage !== 'initial'} className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50" required />
                    </div>
                </div>
                 {processStage === 'initial' && (
                    <div className="text-right">
                        <button onClick={handleProcessPDF} className="bg-blue-600 text-white font-bold py-3 px-8 rounded-lg hover:bg-blue-700 disabled:bg-gray-400" disabled={!flyerFile || isLoading}>
                            1. Process PDF
                        </button>
                    </div>
                )}
            </div>

            {pdfPages.length > 0 && (
                <div className="bg-white p-8 rounded-lg shadow-md mb-8">
                    <h2 className="text-2xl font-semibold mb-4">Step 2: Review Pages</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-6">
                        {pdfPages.map(page => (
                            <div key={page.pageNumber} className="relative border rounded-lg p-2 group">
                                <img src={page.imageDataUrl} alt={`Page ${page.pageNumber}`} className="w-full h-auto" />
                                <p className="text-center text-sm mt-2">Page {page.pageNumber}</p>
                                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button onClick={() => handleDeletePage(page.pageNumber)} className="bg-red-600 text-white rounded-full p-2" title="Delete Page">🗑️</button>
                                </div>
                            </div>
                        ))}
                    </div>
                    {processStage === 'pages_processed' && !isLoading && (
                        <div className="text-right space-x-4">
                            {failedItems.detect.length > 0 && (
                                <>
                                    <span className="text-red-600 font-medium">Detection failed for {failedItems.detect.length} pages.</span>
                                    <button onClick={handleRetryDetect} className="bg-yellow-500 text-white font-bold py-3 px-6 rounded-lg hover:bg-yellow-600">Retry Failed Pages</button>
                                    <button onClick={() => { setFailedItems(prev => ({ ...prev, detect: [] })); setProcessStage('offers_detected'); }} className="bg-gray-500 text-white font-bold py-3 px-6 rounded-lg hover:bg-gray-600">Proceed Anyway</button>
                                </>
                            )}
                            {failedItems.detect.length === 0 && (
                                <button onClick={handleDetectOffers} className="bg-blue-600 text-white font-bold py-3 px-8 rounded-lg hover:bg-blue-700">2. Detect Offers</button>
                            )}
                        </div>
                    )}
                </div>
            )}

{offerBlocks.length > 0 && (
                 <div className="bg-white p-8 rounded-lg shadow-md mb-8">
                    <h2 className="text-2xl font-semibold mb-4">Step 3: Review Detected Offer Blocks ({offerBlocks.length} total)</h2>
                    <p className="text-gray-600 mb-6">Click on a page to edit, add, or delete the offer boxes for that page.</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-6">
                         {Object.values(offerBlocksByPage).map(pageGroup => {
                            const pageCanvas = document.createElement('canvas');
                            const image = new Image();
                            image.src = pageGroup.imageDataUrl;
                            pageCanvas.width = image.width;
                            pageCanvas.height = image.height;
                            const ctx = pageCanvas.getContext('2d');
                            ctx.drawImage(image, 0, 0);
                            ctx.strokeStyle = '#3b82f6';
                            ctx.lineWidth = 5;
                            pageGroup.offerBlocks.forEach(block => {
                                const [ymin, xmin, ymax, xmax] = block.box_2d;
                                ctx.strokeRect(
                                    (xmin / 1000) * image.width,
                                    (ymin / 1000) * image.height,
                                    ((xmax - xmin) / 1000) * image.width,
                                    ((ymax - ymin) / 1000) * image.height
                                );
                            });
                            return (
                                <div key={pageGroup.pageNumber} onClick={() => handleOpenMultiCropEditor(pageGroup.pageNumber)} className="border rounded-lg p-2 group cursor-pointer hover:border-blue-500 hover:shadow-lg transition-all">
                                    <img src={pageCanvas.toDataURL()} alt={`Page ${pageGroup.pageNumber} with offers`} className="w-full h-auto" />
                                    <p className="text-center text-sm mt-2 font-semibold">Page {pageGroup.pageNumber} ({pageGroup.offerBlocks.length} Offers)</p>
                                </div>
                            );
                        })}
                    </div>
                    {processStage === 'offers_detected' && !isLoading && (
                        <div className="text-right space-x-4">
                            {failedItems.extract.length > 0 && (
                                <>
                                    <span className="text-red-600 font-medium">Extraction failed for {failedItems.extract.length} blocks.</span>
                                    <button onClick={handleRetryExtract} className="bg-yellow-500 text-white font-bold py-3 px-6 rounded-lg hover:bg-yellow-600">Retry Failed Blocks</button>
                                    <button onClick={() => { setFailedItems(prev => ({ ...prev, extract: [] })); setProcessStage('details_extracted'); }} className="bg-gray-500 text-white font-bold py-3 px-6 rounded-lg hover:bg-gray-600">Proceed Anyway</button>
                                </>
                            )}
                            {failedItems.extract.length === 0 && (
                                <button onClick={handleExtractDetails} className="bg-blue-600 text-white font-bold py-3 px-8 rounded-lg hover:bg-blue-700">3. Extract Details</button>
                            )}
                        </div>
                    )}
                </div>
            )}

            {productData.length > 0 && (
                 <div>
                    <h2 className="text-2xl font-semibold mb-4">Step 4: Review and Edit Extracted Data</h2>
                    <div className="overflow-x-auto bg-white rounded-lg shadow-md">
                        <table className="w-full text-sm text-left text-gray-600">
                             <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3">Page</th>
                                    <th className="px-4 py-3">Image</th>
                                    <th className="px-4 py-3">Category Path</th>
                                    <th className="px-4 py-3">Vendor (EN/AR)</th>
                                    <th className="px-4 py-3">Product Name (EN/AR)</th>
                                    <th className="px-4 py-3">Qty</th>
                                    <th className="px-4 py-3">Size</th>
                                    <th className="px-4 py-3">Unit</th>
                                    <th className="px-4 py-3">Price</th>
                                    <th className="px-4 py-3">Old Price</th>
                                    <th className="px-4 py-3">Price/Unit</th>
                                    <th className="px-4 py-3">Standard Unit (Calc)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {productData.map((product, index) => (
                                    <tr key={index} className="bg-white border-b">
                                        <td className="px-4 py-4">{product.pageNumber}</td>
                                        <td className="px-4 py-4"><img src={product.offerImage} alt="Offer" className="w-20 h-20 object-contain cursor-pointer" onClick={() => setViewingImage(product.offerImage)}/></td>
                                        <td className="px-2 py-4"><CategorySelector value={product.sub_category || ''} onChange={(newValue) => handleTableEdit(index, 'sub_category', newValue)} categoryTree={categoryTree} /></td>
                                        <td className="px-2 py-4"><input type="text" value={product.vendor_english || ''} onChange={(e) => handleTableEdit(index, 'vendor_english', e.target.value)} className="w-full p-1" /><br/><input type="text" value={product.vendor_arabic || ''} onChange={(e) => handleTableEdit(index, 'vendor_arabic', e.target.value)} className="w-full p-1" /></td>
                                        <td className="px-2 py-4"><input type="text" value={product.english_name || ''} onChange={(e) => handleTableEdit(index, 'english_name', e.target.value)} className="w-full p-1" /><br/><input type="text" value={product.arabic_name || ''} onChange={(e) => handleTableEdit(index, 'arabic_name', e.target.value)} className="w-full p-1" /></td>
                                        <td className="px-2 py-4"><input type="text" value={product.quantity || ''} onChange={(e) => handleTableEdit(index, 'quantity', e.target.value)} className="w-20 p-1" /></td>
                                        <td className="px-2 py-4"><input type="text" value={product.size || ''} onChange={(e) => handleTableEdit(index, 'size', e.target.value)} className="w-20 p-1" /></td>
                                        <td className="px-2 py-4"><input type="text" value={product.unit || ''} onChange={(e) => handleTableEdit(index, 'unit', e.target.value)} className="w-16 p-1" /></td>
                                        <td className="px-2 py-4"><input type="text" value={product.current_price || ''} onChange={(e) => handleTableEdit(index, 'current_price', e.target.value)} className="w-24 p-1" /></td>
                                        <td className="px-2 py-4"><input type="text" value={product.previous_price || ''} onChange={(e) => handleTableEdit(index, 'previous_price', e.target.value)} className="w-24 p-1" /></td>
                                        <td className="px-4 py-4 font-semibold text-blue-600">{product.price_per_unit || 'N/A'}</td>
                                        <td className="px-4 py-4 font-semibold text-blue-600">{product.unit_of_measure || 'N/A'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="flex justify-end gap-4 mt-8">
                        {processStage === 'details_extracted' && !isLoading && (
                            <>
                                {failedItems.categorize.length > 0 && (
                                    <div className="text-right space-x-4 mb-4"> {/* Added mb-4 for spacing */}
                                        <span className="text-red-600 font-medium">Categorization failed for {failedItems.categorize.length} groups.</span>
                                        <button onClick={handleRetryCategorize} className="bg-yellow-500 text-white font-bold py-3 px-6 rounded-lg hover:bg-yellow-600">Retry Failed Groups</button>
                                        <button onClick={() => { setFailedItems(prev => ({ ...prev, categorize: [] })); setProcessStage('categorized'); }} className="bg-gray-500 text-white font-bold py-3 px-6 rounded-lg hover:bg-gray-600">Proceed Anyway</button>
                                    </div>
                                )}
                                {failedItems.categorize.length === 0 && (
                                    <button onClick={handleCategorizeOffers} className="bg-cyan-600 text-white font-bold py-3 px-8 rounded-lg hover:bg-cyan-700">4. Categorize Products</button>
                                )}
                            </>
                        )}
                        {processStage === 'categorized' && (
                            <button onClick={handleSaveFlyerAndPages} className="bg-purple-600 text-white font-bold py-3 px-8 rounded-lg hover:bg-purple-700">5. Save Flyer & Pages</button>
                        )}
                        {processStage === 'flyer_saved' && (
                            <button onClick={handleSaveOffers} className="bg-green-600 text-white font-bold py-3 px-8 rounded-lg hover:bg-green-700">6. Save Offers to Database</button>
                        )}
                    </div>
                </div>
            )}
            {isLoading && (
                <div className="fixed bottom-0 left-0 right-0 p-4 z-50">
                    <div className="max-w-xl mx-auto">
                        <Loader message={loadingMessage} />
                    </div>
                </div>
            )}

            <MultiCropEditorModal 
                isOpen={multiCropModalState.isOpen}
                onClose={() => setMultiCropModalState({ isOpen: false, page: null })}
                onSave={handleSaveMultiCrop}
                page={multiCropModalState.page}
            />

            <ImageModal
                isOpen={!!viewingImage}
                onClose={() => setViewingImage(null)}
                imageUrl={viewingImage}
            />
        </div>
    );
}